import type { Operation, FetchResult } from '@apollo/client';
import { action, orchestrator, type OrchestratorFunction } from 'satcheljs';
import { trace } from 'owa-trace';
import { emitSyncEvent } from 'owa-offline-sync-diagnostics';
import type { QueuedActionContext } from 'owa-queued-actions';
import type { Mutation } from 'owa-graph-schema';

export type QueuedActionOperation = Pick<
    Operation,
    'extensions' | 'operationName' | 'query' | 'variables'
> & {
    context: QueuedActionContext;
};

export type QueuedActionDataResultMessage = {
    op: QueuedActionOperation;
    cachePolicy: 'cache' | 'no-cache';
    idmap: ReadonlyMap<string, string>;
    uuid: string;
    result: FetchResult<Partial<Mutation>, Record<string, any>, Record<string, any>>;
};

export type QueuedActionErrorResultMessage = {
    op: QueuedActionOperation;
    uuid: string;
    error: Error;
};

export const queuedActionDataResult: (
    op: Pick<Operation, 'extensions' | 'operationName' | 'query' | 'variables'> & {
        context: QueuedActionContext;
    },
    cachePolicy: 'cache' | 'no-cache',
    idmap: ReadonlyMap<string, string>,
    uuid: string,
    result: FetchResult<Partial<Mutation>, Record<string, any>, Record<string, any>>
) => QueuedActionDataResultMessage = action(
    'QUEUED_ACTION_DATA_RESULT',
    (
        op: Pick<Operation, 'extensions' | 'operationName' | 'query' | 'variables'> & {
            context: QueuedActionContext;
        },
        cachePolicy: 'cache' | 'no-cache',
        idmap: ReadonlyMap<string, string>,
        uuid: string,
        result: FetchResult<Partial<Mutation>, Record<string, any>, Record<string, any>>
    ): QueuedActionDataResultMessage => ({ op, cachePolicy, idmap, uuid, result })
);

export const queuedActionErrorResult: (
    op: Pick<Operation, 'extensions' | 'operationName' | 'query' | 'variables'> & {
        context: QueuedActionContext;
    },
    uuid: string,
    error: Error
) => QueuedActionErrorResultMessage = action(
    'QUEUED_ACTION_ERROR_RESULT',
    (
        op: Pick<Operation, 'extensions' | 'operationName' | 'query' | 'variables'> & {
            context: QueuedActionContext;
        },
        uuid: string,
        error: Error
    ): QueuedActionErrorResultMessage => ({ op, uuid, error })
);

// the default data acceptor traces the results
export const queuedActionDataResultOrchestrator: OrchestratorFunction<QueuedActionDataResultMessage> =
    orchestrator(queuedActionDataResult, message => {
        trace.info(`handling data result with policy ${message.cachePolicy}`, 'actionQueue');

        const idchanges = Array.from(message.idmap.entries())
            .map(([k, v]) => `${k} -> ${v}`)
            .join(', ');
        const opName = message.op.operationName;
        const uuid = message.uuid;

        emitSyncEvent('actionQueue:dataResult', { uuid, opName, idchanges });
    });

// the default error handler just traces it
export const queuedActionErrorResultOrchestrator: OrchestratorFunction<QueuedActionErrorResultMessage> =
    orchestrator(queuedActionErrorResult, message => {
        trace.info(`handling error result ${message.error.message}`, 'actionQueue');
        const opName = message.op.operationName;
        const uuid = message.uuid;
        const error = message.error?.message;
        emitSyncEvent('actionQueue:errorResult', { uuid, opName, error });
    });
